import { Injectable } from '@angular/core'
import { LocalStorageService } from '@services/local-storage-service'
import { TokenRepositoryService } from '@services/token-repository.service'

type StoredCacheEntry<T> = {
    value: T
    cachedAt: string
}

export type AuthenticatedCacheEntry<T> = {
    value: T
    cachedAt: Date
}

type TokenClaims = {
    sub?: unknown
    username?: unknown
}

@Injectable({ providedIn: 'root' })
export class AuthenticatedCacheService {

    private static readonly CACHE_PREFIX = 'authenticated.cache'

    constructor(
        private localStorageService: LocalStorageService,
        private tokenRepositoryService: TokenRepositoryService
    ) { }

    store<T>(key: string, value: T): void {
        try {
            const cacheKey = this.cacheKey(key)
            if (cacheKey === null) {
                return
            }
            this.localStorageService.setItem(cacheKey, {
                value,
                cachedAt: new Date().toISOString(),
            })
        } catch {
            // Cache writes must not prevent a successful response from being used.
        }
    }

    retrieve<T>(key: string): T | null {
        return this.retrieveEntry<T>(key)?.value ?? null
    }

    retrieveEntry<T>(key: string): AuthenticatedCacheEntry<T> | null {
        try {
            const cacheKey = this.cacheKey(key)
            if (cacheKey === null) {
                return null
            }
            const entry = this.localStorageService.getItem(cacheKey) as StoredCacheEntry<T> | null
            if (!entry || entry.value === undefined || entry.value === null || typeof entry.cachedAt !== 'string') {
                return null
            }
            const cachedAt = new Date(entry.cachedAt)
            if (Number.isNaN(cachedAt.getTime())) {
                return null
            }
            return { value: entry.value, cachedAt }
        } catch {
            return null
        }
    }

    remove(key: string): void {
        try {
            const cacheKey = this.cacheKey(key)
            if (cacheKey === null) {
                return
            }
            this.localStorageService.removeItem(cacheKey)
        } catch {
            // Failed eviction must not interrupt loading fresh data.
        }
    }

    clearAll(): void {
        const prefix = `com.hospitalityproject.${AuthenticatedCacheService.CACHE_PREFIX}.`
        const legacyPrefix = 'com.hospitalityproject.bookings.cache.'
        for (const key of Object.keys(localStorage)) {
            if (key.startsWith(prefix) || key.startsWith(legacyPrefix)) {
                localStorage.removeItem(key)
            }
        }
    }

    clearMatching(keyPrefix: string): void {
        const prefix = `com.hospitalityproject.${AuthenticatedCacheService.CACHE_PREFIX}.`
        for (const key of Object.keys(localStorage)) {
            if (key.startsWith(prefix) && key.includes(`.${keyPrefix}`)) {
                localStorage.removeItem(key)
            }
        }
    }

    private cacheKey(key: string): string | null {
        const subject = this.currentTokenSubject()
        if (subject === null) {
            return null
        }
        return `${AuthenticatedCacheService.CACHE_PREFIX}.${subject}.${key}`
    }

    private currentTokenSubject(): string | null {
        const token = this.tokenRepositoryService.getAccessToken()
        if (token === null) {
            return null
        }
        const encodedClaims = token.split('.')[1]
        if (encodedClaims === undefined) {
            return null
        }
        try {
            const claims = JSON.parse(this.decodeBase64Url(encodedClaims)) as TokenClaims
            if (typeof claims.sub === 'string') {
                return claims.sub
            }
            if (typeof claims.username === 'string') {
                return claims.username
            }
            return null
        } catch {
            return null
        }
    }

    private decodeBase64Url(value: string): string {
        const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
        return atob(padded)
    }
}
